import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUturnLeftIcon, CheckCircleIcon, ChevronLeftIcon, ClockIcon, DocumentTextIcon, ExclamationTriangleIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Modal from '../components/Modal';
import DocumentViewer from './DocumentViewer';
import AbsenceRecordEditor, { QuestionField } from './AbsenceRecordEditor';
import { employeeOptionsFromDirectory } from './EmployeeLookup';
import { useAbsenceReportingSubmissions, useAbsenceReportingSubmission } from '../hooks/useAbsenceReporting';
import { useDirectory } from '../hooks/useDirectory';
import { NODES, getVisiblePath, groupBySection } from './absenceReportingFlow';
import { REPORT_TYPE_LABEL } from './absenceReportingCategories';
import { toUkDateText, toUkTimeText } from '../utils/ukTime';

// Managers > Absence Reporting (06/10/2026) — office managers report absence,
// lateness and returns to work for their own people from the web, through the
// SAME FSM endpoints engineers' supervisors use (via this app's
// /api/absence-reporting proxy), so every report lands in HR's Absence & Late
// Reporting exactly as an FSM one does. The FSM server scopes a manager to the
// records they reported themselves; HR / admins using this page see their own
// reports too (filtered below by reporter), never everyone's.

const SUBMIT_TYPES = [
  { reportType: 'Report Absence', label: 'Report Absence', description: 'Someone is off today.', icon: ExclamationTriangleIcon },
  { reportType: 'Report Late', label: 'Report Late', description: 'Someone arrived late.', icon: ClockIcon },
];

function parseUkDate(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || '');
  return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
}

function whenText(row) {
  const d = new Date(row.event_at || row.submitted_at);
  return `${toUkDateText(d)} ${toUkTimeText(d)}`;
}

// Everyone off or late right now (FSM getTeamActive): who, what kind, since
// when and who reported it — never the reason, notes or day counts.
function useTeamActive() {
  return useQuery({
    queryKey: ['absence-reporting-team-active'],
    queryFn: async () => (await api.get('/api/absence-reporting/team-active')).data.rows || [],
    refetchInterval: 5 * 60 * 1000,
  });
}

function useSubmitAbsence() {
  return useMutation({ mutationFn: async (answers) => (await api.post('/api/absence-reporting', answers)).data });
}

function useReturnToWork() {
  return useMutation({
    mutationFn: async ({ id, ...answers }) => (await api.put(`/api/absence-reporting/${id}/return-to-work`, answers)).data,
  });
}

// One report, start to finish — the same questions and branching as FSM's
// AbsenceReportingForm.jsx (absenceReportingFlow.js is a copy of FSM's), all
// sections open. A Return to Work closes the absence it belongs to (absenceId)
// in place, exactly as FSM does.
function ReportForm({ reportType, absenceId, employeeEmail, onClose, onView }) {
  const queryClient = useQueryClient();
  const isReturnToWork = reportType === 'Return to Work';
  const { data: directory } = useDirectory();
  const { data: existing, isLoading: loadingExisting } = useAbsenceReportingSubmission(absenceId, Boolean(absenceId));
  const submit = useSubmitAbsence();
  const returnToWork = useReturnToWork();
  const [answers, setAnswers] = useState(() => ({ ongoingAbsence: 'No', reportType, ...(employeeEmail ? { employeeEmail } : {}) }));
  const [validationError, setValidationError] = useState('');
  const [savedId, setSavedId] = useState(null);

  // The absence being closed: its own answers (employee, department) come
  // across once; Meeting Date defaults to today and Absence Start Date to when
  // the absence happened — both still editable.
  const merged = useRef(false);
  useEffect(() => {
    if (!isReturnToWork || !existing?.answers || merged.current) return;
    merged.current = true;
    const { reportType: _stored, ...rest } = existing.answers;
    setAnswers((prev) => ({
      ...prev,
      ...rest,
      meetingDate: prev.meetingDate || rest.meetingDate || toUkDateText(new Date()),
      absenceStartDate: prev.absenceStartDate || rest.absenceStartDate || toUkDateText(new Date(existing.eventAt || existing.submittedAt)),
    }));
  }, [isReturnToWork, existing]);

  const employeeOptions = useMemo(
    () => employeeOptionsFromDirectory(directory, [answers.employeeEmail]),
    [directory, answers.employeeEmail]
  );

  const { path, terminal } = getVisiblePath(answers);
  const visiblePath = path.filter((id) => !NODES[id].hidden);
  const sections = groupBySection(visiblePath);

  function updateAnswer(field, value) {
    setValidationError('');
    setAnswers((a) => {
      const next = { ...a, [field]: value };
      // Days Absent: inclusive of the start date, exclusive of the end date
      // (the day they came back) — same count as FSM's form.
      if (field === 'absenceStartDate' || field === 'absenceEndDate') {
        const start = parseUkDate(next.absenceStartDate);
        const end = parseUkDate(next.absenceEndDate);
        if (start && end && end >= start) next.daysAbsent = String(Math.round((end - start) / 86400000));
      }
      return next;
    });
  }

  async function handleSubmit() {
    setValidationError('');
    if (!terminal) {
      const missing = NODES[path[path.length - 1]];
      setValidationError(`Please answer: "${missing.section} — ${missing.text}"`);
      return;
    }
    const start = parseUkDate(answers.absenceStartDate);
    const end = parseUkDate(answers.absenceEndDate);
    if (isReturnToWork && start && end && end < start) {
      setValidationError('Absence End Date is before Absence Start Date — please check the dates.');
      return;
    }
    try {
      if (isReturnToWork) {
        await returnToWork.mutateAsync({ id: absenceId, ...answers });
        setSavedId(Number(absenceId));
      } else {
        const result = await submit.mutateAsync(answers);
        setSavedId(result.id);
      }
      queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['absence-reporting-team-active'] });
    } catch {
      // Rendered below.
    }
  }

  const saving = submit.isPending || returnToWork.isPending;
  const saveError = submit.error || returnToWork.error;
  const saveErrorText = saveError?.response?.data?.error || (saveError ? 'Could not save — please try again.' : '');
  const title = isReturnToWork ? 'Return to Work' : reportType;

  const footer = savedId ? (
    <div className="flex flex-wrap justify-end gap-2">
      <button
        type="button"
        onClick={() => onView(
          `/api/absence-reporting/${savedId}/report.pdf${isReturnToWork ? '?section=returnToWork' : ''}`,
          REPORT_TYPE_LABEL[reportType]
        )}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        <DocumentTextIcon className="h-4 w-4" />
        View report
      </button>
      <button type="button" onClick={onClose} className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">Done</button>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-3">
      {(validationError || saveErrorText) && <p className="mr-auto text-xs text-rose-700">{validationError || saveErrorText}</p>}
      <button type="button" onClick={onClose} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={saving || (absenceId && loadingExisting)}
        className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
      >
        {saving ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  );

  return (
    <Modal title={title} subtitle="Goes straight to HR, the same as a report filed in FSM." onClose={onClose} footer={footer} width="max-w-2xl">
      {savedId ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <CheckCircleIcon className="h-10 w-10 text-green-600" />
          <p className="text-sm font-semibold text-gray-900">{title} submitted</p>
          <p className="text-xs text-gray-500">HR can see it now. It's also listed under your reports.</p>
        </div>
      ) : absenceId && loadingExisting ? (
        <p className="p-5 text-sm text-gray-400">Loading the absence…</p>
      ) : (
        <div className="space-y-3 p-5">
          {sections.map((group) => (
            <section key={group.section} className="rounded-lg border border-gray-200 bg-white">
              <p className="border-b border-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700">{group.section}</p>
              <div className="space-y-4 p-4">
                {group.nodeIds.map((nodeId) => (
                  <QuestionField
                    key={nodeId}
                    node={NODES[nodeId]}
                    value={answers[NODES[nodeId].field]}
                    onChange={(v) => updateAnswer(NODES[nodeId].field, v)}
                    employeeOptions={employeeOptions}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}

function ReportButtons({ row, onView }) {
  const btn = 'inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50';
  return (
    <div className="flex flex-wrap gap-1.5">
      <button type="button" className={btn} onClick={() => onView(`/api/absence-reporting/${row.id}/report.pdf`, REPORT_TYPE_LABEL[row.report_type])}>
        <DocumentTextIcon className="h-3.5 w-3.5" />
        {REPORT_TYPE_LABEL[row.report_type] || 'Report'}
      </button>
      {row.returned_to_work_at && (
        <button type="button" className={btn} onClick={() => onView(`/api/absence-reporting/${row.id}/report.pdf?section=returnToWork`, 'Return to Work Report')}>
          <DocumentTextIcon className="h-3.5 w-3.5" />
          Return to Work Report
        </button>
      )}
    </div>
  );
}

function statusOf(row) {
  if (row.report_type === 'Report Late') return { text: 'Late', tone: 'bg-amber-50 text-amber-700' };
  if (row.returned_to_work_at) return { text: `Returned ${toUkDateText(new Date(row.returned_to_work_at))}`, tone: 'bg-green-50 text-green-700' };
  return { text: 'Still absent', tone: 'bg-rose-50 text-rose-700' };
}

function ManagerAbsenceReporting() {
  const { instance, accounts } = useMsal();
  const me = ((instance.getActiveAccount() || accounts[0])?.username || '').toLowerCase();
  const { data: rows, isLoading, error } = useAbsenceReportingSubmissions();
  const { data: directory } = useDirectory();
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [viewerBlock, setViewerBlock] = useState(null);
  const onView = (url, title) => setViewerBlock({ source: 'api-fetch', url, title });

  const nameOf = useMemo(() => {
    const map = new Map();
    for (const p of directory?.people || []) if (p.email) map.set(p.email.toLowerCase(), p.display_name || p.email);
    return (email) => map.get(String(email || '').toLowerCase()) || email || '—';
  }, [directory]);

  // Mine only, current versions only (an edited record's original is
  // superseded; one HR discarded is archived).
  const mine = useMemo(
    () => (rows || []).filter((r) => String(r.engineer_email || '').toLowerCase() === me && !r.superseded_at && !r.archived_at),
    [rows, me]
  );
  const { data: team, isLoading: teamLoading, error: teamError } = useTeamActive();
  const offNow = (team || []).filter((r) => r.report_type === 'Report Absence');
  const lateToday = (team || []).filter((r) => r.report_type === 'Report Late');

  return (
    <div className="p-4 sm:p-8">
      <Link to="/?dept=managers" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" />
        Back to Managers
      </Link>

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Absence Reporting</h1>
        <p className="mt-1 text-sm text-gray-500">
          Report an absence or a late arrival for someone in your team, and complete their return to work. HR sees every report as soon as it's submitted.
        </p>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        {SUBMIT_TYPES.map((t) => (
          <button
            key={t.reportType}
            type="button"
            onClick={() => setForm({ reportType: t.reportType })}
            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-brand hover:shadow-sm"
          >
            <t.icon className="h-8 w-8 shrink-0 text-brand" />
            <span>
              <span className="block text-sm font-semibold text-gray-900">{t.label}</span>
              <span className="block text-xs text-gray-500">{t.description}</span>
            </span>
          </button>
        ))}
      </div>

      {error && (
        <p className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Could not load your reports: {error.response?.data?.error || error.message}
        </p>
      )}

      {/* Team view (06/10/2026): everyone off or late now — HR's Today and
          Pending without the reasons, notes or day counts. Return to Work
          only on absences this manager reported (FSM refuses anyone else's). */}
      <section className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Off now <span className="font-normal text-gray-400">({offNow.length})</span></p>
          <p className="text-xs text-gray-500">Everyone reported absent who hasn't returned yet, including anyone reported today.</p>
        </div>
        {teamError ? (
          <p className="px-4 py-4 text-sm text-rose-700">Could not load: {teamError.response?.data?.error || teamError.message}</p>
        ) : teamLoading ? (
          <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>
        ) : offNow.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-500">Nobody is reported absent.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Department</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Off since</th>
                  <th className="px-4 py-2">Reported by</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offNow.map((r) => {
                  const since = new Date(r.opened_at);
                  const newToday = toUkDateText(since) === toUkDateText(new Date());
                  return (
                    <tr key={r.id}>
                      <td className="px-4 py-2.5 font-medium text-gray-900">
                        {nameOf(r.employee_email)}
                        {newToday && <span className="ml-1.5 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">Reported today</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{r.department_area || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{r.absence_category || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">{toUkDateText(since)}</td>
                      <td className="px-4 py-2.5 text-gray-600">{r.reported_by || '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {r.reported_by_email === me ? (
                          <button
                            type="button"
                            onClick={() => setForm({ reportType: 'Return to Work', absenceId: r.id, employeeEmail: r.employee_email })}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-dark"
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4" />
                            Return to Work
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Late today <span className="font-normal text-gray-400">({lateToday.length})</span></p>
          <p className="text-xs text-gray-500">Late arrivals reported today.</p>
        </div>
        {teamLoading ? (
          <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>
        ) : lateToday.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-500">No late arrivals reported today.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Department</th>
                  <th className="px-4 py-2">Arrived</th>
                  <th className="px-4 py-2">Reported by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lateToday.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{nameOf(r.employee_email)}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.department_area || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.employee_time_of_arrival || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.reported_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">My reports</p>
          <p className="text-xs text-gray-500">Everything you've reported. Editing saves a new, amended entry — the original stays on record.</p>
        </div>
        {isLoading ? (
          <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>
        ) : mine.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-500">You haven't reported anything yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Reports</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mine.map((r) => {
                  const status = statusOf(r);
                  return (
                    <tr key={r.id} className="align-top">
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">{whenText(r)}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{nameOf(r.employee_email)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                        {r.report_type}
                        {r.amended_from_id && <span className="ml-1 text-xs text-gray-400">(edited)</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.tone}`}>{status.text}</span>
                      </td>
                      <td className="px-4 py-2.5"><ReportButtons row={r} onView={onView} /></td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => setEditingId(r.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:text-brand-dark"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && <ReportForm {...form} onClose={() => setForm(null)} onView={onView} />}
      {editingId && <AbsenceRecordEditor id={editingId} onClose={() => setEditingId(null)} />}
      {viewerBlock && <DocumentViewer block={viewerBlock} onClose={() => setViewerBlock(null)} />}
    </div>
  );
}

export default ManagerAbsenceReporting;
